import { logInfo } from "@/Utils/logger";

/**
 * How long `handleSendAvatarRoute` may spend on Firestore in total before the
 * splash gives up waiting and renders the app anyway.
 *
 * Measured on prod (2026-09-01→04, n=450 page loads that cleared the splash):
 * the auth-user → splash-cleared window is p50 808 ms, p90 4.5 s, p95 6.7 s,
 * p99 13.1 s, max 18.6 s. The stalls are a separate, disjoint cluster at
 * 30.0–31.5 s — a transport timeout, not a slow tail — and there is nothing at
 * all between 18.6 s and 30.3 s. 10 s therefore sits above the p99 of healthy
 * sessions and far below every stall.
 *
 * It is deliberately NOT set above the 18.6 s observed max, which is the rule
 * `[2.5.13]`'s finalize watchdog followed. That rule exists because aborting a
 * finalize destroys a scan; here the fallback is benign — see `withinBudget`.
 * Raise it only if `read timed out` starts appearing on sessions that would
 * have succeeded.
 */
export const SPLASH_READ_BUDGET_MS = 10_000;

export interface SplashBudget {
  /** Milliseconds left before the whole route must stop waiting. */
  remainingMs(): number;
}

/**
 * One deadline for the WHOLE route, not per read. `handleSendAvatarRoute`
 * awaits up to four Firestore calls in sequence, so a per-read timeout would
 * bound the splash at 4× the budget. Sharing one deadline is what makes the
 * guarantee "the splash never waits longer than this" true.
 */
export function createSplashBudget(
  totalMs: number = SPLASH_READ_BUDGET_MS,
  now: () => number = Date.now,
): SplashBudget {
  const startedAt = now();
  return {
    remainingMs: () => Math.max(0, totalMs - (now() - startedAt)),
  };
}

/**
 * Race one Firestore read against the route's shared deadline, resolving to
 * `fallback` if the deadline wins.
 *
 * This is a `Promise.race` rather than the watchdog pattern `useCamera` uses,
 * and the difference is deliberate: there, discarding a slow-but-successful
 * `getUserMedia` would cost the user their camera, so the timer only observes.
 * Here every caller already has a defined answer for "this read did not work"
 * — its existing `catch` — so adopting that answer early is safe. The losing
 * read is left running; it is a read, so nothing depends on cancelling it.
 *
 * Rejections are NOT converted to the fallback. The caller's `try/catch` is the
 * real error path and must keep seeing real Firestore errors.
 */
export function withinBudget<T, F = T>(
  read: Promise<T>,
  budget: SplashBudget,
  fallback: F,
  label: string,
): Promise<T | F> {
  const remainingMs = budget.remainingMs();

  if (remainingMs <= 0) {
    logInfo("[App] send-avatar read timed out — budget already spent", {
      read: label,
      remainingMs: 0,
    });
    return Promise.resolve(fallback);
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<F>((resolve) => {
    timer = setTimeout(() => {
      logInfo("[App] send-avatar read timed out — rendering without it", {
        read: label,
        remainingMs,
      });
      resolve(fallback);
    }, remainingMs);
  });

  return Promise.race<T | F>([read, expiry]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}
